import type { ReactNode } from "react";
import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import { PolarPlot } from "@/components/tools/linear-algebra/polar-plot";
import type { POLAR_COORDINATES_CONVERTER_STRINGS } from "@/components/tools/polar-coordinates-converter";
import { t } from "@/lib/i18n/interpolate";
import type { AngleUnit } from "@/lib/tool-kit/linear-algebra/angle-input";
import { piMultiple } from "@/lib/tool-kit/linear-algebra/exact-angle";
import {
  type Angle,
  type Coord,
  cylindricalToRect,
  polarToRect,
  type Rect3D,
  rectTo3D,
  rectToPolar,
  sphericalToRect,
} from "@/lib/tool-kit/linear-algebra/polar";
import {
  formatQuad,
  negQ,
  type QuadNumber,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import {
  addR,
  formatStyled,
  isZeroR,
  mulR,
  type NumberStyle,
  type Rational,
  signR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

type Strings = typeof POLAR_COORDINATES_CONVERTER_STRINGS;
export type Convention = "physics" | "calculus";

/** Symbols for each convention; names are letters, not prose, so they are not translated. */
export const SYMBOLS = {
  physics: {
    cylRadius: "ρ",
    cylAngle: "φ",
    sphRadius: "r",
    sphPolar: "θ",
    sphAzimuth: "φ",
  },
  calculus: {
    cylRadius: "r",
    cylAngle: "θ",
    sphRadius: "ρ",
    sphPolar: "φ",
    sphAzimuth: "θ",
  },
} as const;

export interface PolarCtx {
  strings: Strings;
  style: NumberStyle;
  mark: string;
  unit: AngleUnit;
  convention: Convention;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}

const NEEDS_BRACKETS = /^−|\//;

function val(ctx: PolarCtx, x: Rational): string {
  return formatStyled(x, ctx.style, ctx.mark);
}

function br(ctx: PolarCtx, x: Rational): string {
  const text = val(ctx, x);
  return NEEDS_BRACKETS.test(text) ? `(${text})` : text;
}

const LEADING_HYPHEN = /^-/;

/** The formatter's ASCII hyphen as a real minus sign. */
function minus(text: string): string {
  return text.replace(LEADING_HYPHEN, "−");
}

function decimal(ctx: PolarCtx, value: number): string {
  return minus(
    ctx.fmt(Math.abs(value) < 1e-12 ? 0 : value, { maximumFractionDigits: 6 })
  );
}

function quadText(ctx: PolarCtx, q: QuadNumber, value: number): string {
  const exact = formatQuad(q, ctx.style, ctx.mark);
  return isZeroR(q.b) ? exact : `${exact} ≈ ${decimal(ctx, value)}`;
}

function coordText(ctx: PolarCtx, c: Coord): string {
  if (c.exact === null) {
    return decimal(ctx, c.value);
  }
  return quadText(ctx, c.exact, c.value);
}

/** An angle in the chosen unit first; the other in brackets; exact where it is. */
function angleText(ctx: PolarCtx, angle: Angle): string {
  const { strings } = ctx;
  const deg = (value: string) => t(strings.degreesUnit, { value });
  const rad = (value: string) => t(strings.radiansUnit, { value });
  if (angle.exactDegrees !== null) {
    const d = deg(minus(ctx.fmt(angle.exactDegrees)));
    const r = rad(piMultiple(angle.exactDegrees));
    return ctx.unit === "degrees" ? `${d} = ${r}` : `${r} = ${d}`;
  }
  const d = deg(minus(ctx.fmt(angle.degrees, { maximumFractionDigits: 4 })));
  const r = rad(
    minus(
      ctx.fmt((angle.degrees * Math.PI) / 180, { maximumFractionDigits: 6 })
    )
  );
  return ctx.unit === "degrees" ? `${d} ≈ ${r}` : `${r} ≈ ${d}`;
}

function shifted(angle: Angle, by: number): Angle {
  return {
    degrees: angle.degrees + by,
    exactDegrees: angle.exactDegrees === null ? null : angle.exactDegrees + by,
  };
}

function WorkingBox({ lines }: { lines: string[] }) {
  return (
    <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

function Plot({
  x,
  y,
  angle,
  ctx,
}: {
  x: number;
  y: number;
  angle: Angle | null;
  ctx: PolarCtx;
}) {
  const { strings } = ctx;
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.plotHeading}</h3>
      <PolarPlot
        angleLabel={
          angle
            ? t(strings.degreesUnit, {
                value: ctx.fmt(
                  ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360,
                  { maximumFractionDigits: 2 }
                ),
              })
            : ""
        }
        downloadLabel={strings.downloadPng}
        pointLabel={t(strings.pointText, {
          x: decimal(ctx, x),
          y: decimal(ctx, y),
        })}
        title={strings.plotTitle}
        x={x}
        y={y}
      />
    </>
  );
}

/* ------------------------------------------------------- rectangular → polar */

export function RectToPolarResult({
  x,
  y,
  ctx,
}: {
  x: Rational;
  y: Rational;
  ctx: PolarCtx;
}) {
  const { strings } = ctx;
  const p = rectToPolar(x, y);
  if (p.angle === null) {
    return <Notice>{strings.origin}</Notice>;
  }
  const radiusText = quadText(ctx, p.radius, p.radiusValue);
  const where =
    p.quadrant === 0
      ? strings.axisNames[
          String(Math.round(p.angle.degrees)) as "0" | "90" | "180" | "270"
        ]
      : t(strings.quadrantN, { n: strings.quadrants[p.quadrant] });
  const exactSigned =
    p.angle.exactDegrees !== null && p.angle.exactDegrees > 180
      ? p.angle.exactDegrees - 360
      : p.angle.exactDegrees;
  const signedAngle: Angle = {
    degrees: p.signedDegrees ?? 0,
    exactDegrees: exactSigned,
  };
  const second =
    p.angle.degrees >= 180 ? shifted(p.angle, -180) : shifted(p.angle, 180);
  const lines = [
    t(strings.radiusWorking, {
      x: br(ctx, x),
      y: br(ctx, y),
      sum: val(ctx, addR(mulR(x, x), mulR(y, y))),
      result: radiusText,
    }),
    t(strings.angleWorking, {
      y: val(ctx, y),
      x: val(ctx, x),
      result: angleText(ctx, p.angle),
    }),
  ];
  if (p.quadrant !== 0 && p.referenceDegrees !== null) {
    lines.push(
      t(strings.referenceWorking, {
        ref: ctx.fmt(p.referenceDegrees, { maximumFractionDigits: 4 }),
        quadrant: strings.quadrants[p.quadrant],
      })
    );
  }
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.radiusLabel} value={radiusText} />
        <StatCard
          emphasis
          label={strings.thetaLabel}
          value={angleText(ctx, p.angle)}
        />
        <StatCard label={strings.locationLabel} value={where} />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={`(r, θ) = (${formatQuad(p.radius, ctx.style, ctx.mark)}, ${angleText(ctx, p.angle)})`}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <WorkingBox lines={lines} />
      <p className="text-muted-foreground text-sm">
        {t(strings.equivalentForms, {
          signed: angleText(ctx, signedAngle),
          full: angleText(ctx, shifted(p.angle, 360)),
          r: formatQuad(p.radius, ctx.style, ctx.mark),
          negr: formatQuad(negQ(p.radius), ctx.style, ctx.mark),
          second: angleText(ctx, second),
        })}
      </p>
      <Plot angle={p.angle} ctx={ctx} x={toNumberR(x)} y={toNumberR(y)} />
    </>
  );
}

/* ------------------------------------------------------- polar → rectangular */

export function PolarToRectResult({
  r,
  angle,
  ctx,
}: {
  r: Rational;
  angle: Angle;
  ctx: PolarCtx;
}) {
  const { strings } = ctx;
  const p = polarToRect(r, angle);
  const xText = coordText(ctx, p.x);
  const yText = coordText(ctx, p.y);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label="x = r cos θ" value={xText} />
        <StatCard emphasis label="y = r sin θ" value={yText} />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={`(${xText}, ${yText})`}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <WorkingBox
        lines={[
          t(strings.polarWorking, {
            r: br(ctx, r),
            angle: angleText(ctx, angle),
          }),
          t(strings.xWorking, { x: xText }),
          t(strings.yWorking, { y: yText }),
          ...(p.x.exact === null ? [strings.decimalNote] : []),
        ]}
      />
      {signR(r) < 0 ? (
        <p className="text-muted-foreground text-sm">
          {strings.negativeRadius}
        </p>
      ) : null}
      <Plot angle={angle} ctx={ctx} x={p.x.value} y={p.y.value} />
    </>
  );
}

/* ------------------------------------------------------------------- 3D */

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={`${CARD} space-y-3`}>
      <p className="font-semibold text-foreground">{title}</p>
      {children}
    </div>
  );
}

export function RectToSpaceResult({
  x,
  y,
  z,
  ctx,
}: {
  x: Rational;
  y: Rational;
  z: Rational;
  ctx: PolarCtx;
}) {
  const { strings } = ctx;
  const s = SYMBOLS[ctx.convention];
  const c = rectTo3D(x, y, z);
  const rho = quadText(ctx, c.rho, c.rhoValue);
  const radius = quadText(ctx, c.radius, c.radiusValue);
  const azimuth = c.azimuth
    ? angleText(ctx, c.azimuth)
    : strings.undefinedAngle;
  const polar = c.polar ? angleText(ctx, c.polar) : strings.undefinedAngle;
  const cylindrical = `(${rho}, ${azimuth}, ${val(ctx, z)})`;
  // Physics orders the angles (polar, azimuth); the calculus texts write (azimuth, polar).
  const sphere =
    ctx.convention === "physics"
      ? [
          { symbol: s.sphRadius, value: radius },
          { symbol: s.sphPolar, value: polar },
          { symbol: s.sphAzimuth, value: azimuth },
        ]
      : [
          { symbol: s.sphRadius, value: radius },
          { symbol: s.sphAzimuth, value: azimuth },
          { symbol: s.sphPolar, value: polar },
        ];
  const spherical = `(${sphere.map((e) => e.value).join(", ")})`;
  const lines = [
    t(strings.rhoWorking, {
      symbol: s.cylRadius,
      x: br(ctx, x),
      y: br(ctx, y),
      result: rho,
    }),
    t(strings.azimuthWorking, {
      symbol: s.cylAngle,
      y: val(ctx, y),
      x: val(ctx, x),
      result: azimuth,
    }),
    t(strings.radiusWorking3D, {
      symbol: s.sphRadius,
      x: br(ctx, x),
      y: br(ctx, y),
      z: br(ctx, z),
      result: radius,
    }),
    t(strings.polarWorking3D, {
      symbol: s.sphPolar,
      z: val(ctx, z),
      radius: formatQuad(c.radius, ctx.style, ctx.mark),
      result: polar,
    }),
  ];
  return (
    <>
      <Card
        title={t(strings.cylindricalTitle, {
          symbols: `(${s.cylRadius}, ${s.cylAngle}, z)`,
        })}
      >
        <StatGrid>
          <StatCard emphasis label={s.cylRadius} value={rho} />
          <StatCard emphasis label={s.cylAngle} value={azimuth} />
          <StatCard emphasis label="z" value={val(ctx, z)} />
        </StatGrid>
      </Card>
      <Card
        title={t(strings.sphericalTitle, {
          symbols: `(${sphere.map((e) => e.symbol).join(", ")})`,
        })}
      >
        <StatGrid>
          {sphere.map((e) => (
            <StatCard
              emphasis
              key={e.symbol + e.value}
              label={e.symbol}
              value={e.value}
            />
          ))}
        </StatGrid>
      </Card>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={`${strings.cylindricalShort}: ${cylindrical}\n${strings.sphericalShort}: ${spherical}`}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <WorkingBox lines={lines} />
      {c.azimuth === null ? (
        <p className="text-muted-foreground text-sm">{strings.onAxisNote}</p>
      ) : null}
      <p className="text-muted-foreground text-sm">
        {strings.conventionNote[ctx.convention]}
      </p>
    </>
  );
}

function RectOutput({ p, ctx }: { p: Rect3D; ctx: PolarCtx }) {
  const { strings } = ctx;
  const x = coordText(ctx, p.x);
  const y = coordText(ctx, p.y);
  const z = coordText(ctx, p.z);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label="x" value={x} />
        <StatCard emphasis label="y" value={y} />
        <StatCard emphasis label="z" value={z} />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={`(${x}, ${y}, ${z})`}
      />
    </>
  );
}

export function CylToRectResult({
  rho,
  angle,
  z,
  ctx,
}: {
  rho: Rational;
  angle: Angle;
  z: Rational;
  ctx: PolarCtx;
}) {
  const { strings } = ctx;
  const s = SYMBOLS[ctx.convention];
  const p = cylindricalToRect(rho, angle, z);
  return (
    <>
      <RectOutput ctx={ctx} p={p} />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <WorkingBox
        lines={[
          t(strings.cylInputs, {
            r: s.cylRadius,
            a: s.cylAngle,
            radius: br(ctx, rho),
            angle: angleText(ctx, angle),
          }),
          t(strings.cylX, {
            r: s.cylRadius,
            a: s.cylAngle,
            x: coordText(ctx, p.x),
          }),
          t(strings.cylY, {
            r: s.cylRadius,
            a: s.cylAngle,
            y: coordText(ctx, p.y),
          }),
          t(strings.cylZ, { z: val(ctx, z) }),
          ...(p.x.exact === null ? [strings.decimalNote] : []),
        ]}
      />
    </>
  );
}

export function SphToRectResult({
  r,
  polar,
  azimuth,
  ctx,
}: {
  r: Rational;
  polar: Angle;
  azimuth: Angle;
  ctx: PolarCtx;
}) {
  const { strings } = ctx;
  const s = SYMBOLS[ctx.convention];
  const p = sphericalToRect(r, polar, azimuth);
  const exact = p.x.exact !== null && p.y.exact !== null && p.z.exact !== null;
  return (
    <>
      <RectOutput ctx={ctx} p={p} />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <WorkingBox
        lines={[
          t(strings.sphToRectFormula, {
            r: s.sphRadius,
            p: s.sphPolar,
            a: s.sphAzimuth,
          }),
          t(strings.sphToRectWorking, {
            radius: br(ctx, r),
            polar: angleText(ctx, polar),
            azimuth: angleText(ctx, azimuth),
          }),
          ...(exact ? [] : [strings.decimalNote]),
        ]}
      />
      <p className="text-muted-foreground text-sm">
        {strings.conventionNote[ctx.convention]}
      </p>
    </>
  );
}
